import { useEffect, useMemo, useState } from "react";
import { Plus, Bell, BellRing, CheckCircle2, Circle, Repeat } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CATEGORIES, DAYS, IconFor } from "@/lib/constants";
import ActivityDialog from "@/components/ActivityDialog";
import * as notif from "@/lib/notifications";

function fmt(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "pm" : "am";
  const hr = ((h + 11) % 12) + 1;
  return `${hr}:${m.toString().padStart(2, "0")} ${period}`;
}

export default function Planner() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [permission, setPermission] = useState(notif.permissionState());

  const load = async () => {
    try {
      const { data } = await api.get("/activities");
      setActivities(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Schedule browser notifications whenever activities or permission change
  useEffect(() => {
    notif.scheduleForToday(activities);
    return () => notif.clearAllReminders();
  }, [activities, permission]);

  const enableReminders = async () => {
    const state = await notif.requestPermission();
    setPermission(state);
    if (state === "granted") {
      const n = notif.scheduleForToday(activities);
      toast.success(n > 0 ? `Reminders on. ${n} scheduled for today.` : "Reminders are on.");
    } else if (state === "denied") {
      toast.error("Notifications are blocked. You can allow them in your browser settings.");
    }
  };

  const byDay = useMemo(() => {
    const map = Object.fromEntries(DAYS.map((d) => [d.key, []]));
    for (const a of activities) if (map[a.day]) map[a.day].push(a);
    for (const key of Object.keys(map)) map[key].sort((a, b) => a.start_time.localeCompare(b.start_time));
    return map;
  }, [activities]);

  const openNew = (day) => { setEditing(day ? { day } : null); setDialogOpen(true); };
  const openEdit = (a) => { setEditing(a); setDialogOpen(true); };

  const handleSave = async (result) => {
    try {
      if (result.mode === "create") {
        const { data } = await api.post("/activities", result.payload);
        const arr = Array.isArray(data) ? data : [data];
        setActivities((prev) => [...prev, ...arr]);
        toast.success(arr.length > 1 ? `Added on ${arr.length} days.` : "Added to your week.");
      } else {
        const { data } = await api.patch(
          `/activities/${result.id}?scope=${result.scope}`,
          result.patch,
        );
        const arr = Array.isArray(data) ? data : [data];
        setActivities((prev) => {
          const ids = new Set(arr.map((x) => x.id));
          return prev.map((x) => (ids.has(x.id) ? arr.find((y) => y.id === x.id) : x));
        });
        toast.success(result.scope === "series" ? "Saved on all repeating days." : "Saved.");
      }
      setDialogOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleDelete = async ({ id, scope }) => {
    try {
      await api.delete(`/activities/${id}?scope=${scope}`);
      if (scope === "series") {
        // find series_id from current state
        const target = activities.find((x) => x.id === id);
        const sid = target?.series_id;
        setActivities((prev) => prev.filter((x) => (sid ? x.series_id !== sid : x.id !== id)));
        toast.success("Whole routine removed.");
      } else {
        setActivities((prev) => prev.filter((x) => x.id !== id));
        toast.success("Removed.");
      }
      setDialogOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleDone = async (a) => {
    try {
      const { data } = await api.patch(`/activities/${a.id}?scope=one`, { done: !a.done });
      const arr = Array.isArray(data) ? data : [data];
      const updated = arr[0];
      setActivities((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const hasReminderToday = useMemo(() => {
    const today = ["sun","mon","tue","wed","thu","fri","sat"][new Date().getDay()];
    return activities.some((a) => a.reminder && a.day === today && !a.done);
  }, [activities]);

  return (
    <div>
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3 mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-slate-800">Your week</h1>
          <p className="text-slate-600 mt-1">Add activities. Pick many days to repeat. Tap to edit. Tick when done.</p>
        </div>
        <div className="flex gap-2">
          {notif.isSupported() && permission !== "granted" && hasReminderToday && (
            <Button
              variant="outline"
              className="rounded-full border-amber-300 text-amber-800 hover:bg-amber-50"
              onClick={enableReminders}
              data-testid="enable-reminders-btn"
            >
              <BellRing className="w-4 h-4 mr-1" /> Turn on reminders
            </Button>
          )}
          <Button onClick={() => openNew(null)} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-activity-btn">
            <Plus className="w-4 h-4 mr-1" /> Add activity
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading your week...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4" data-testid="week-grid">
          {DAYS.map((d) => (
            <div key={d.key} data-testid={`day-column-${d.key}`} className="day-column bg-white rounded-2xl border border-slate-200 p-3 min-h-40">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading font-bold text-slate-800">{d.label}</h2>
                <button
                  onClick={() => openNew(d.key)}
                  data-testid={`add-activity-${d.key}`}
                  className="no-print w-7 h-7 rounded-full border border-slate-200 hover:bg-amber-50 hover:border-amber-300 flex items-center justify-center text-slate-500"
                  aria-label={`Add activity to ${d.label}`}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-2">
                {byDay[d.key].length === 0 && (
                  <p className="text-xs text-slate-400 italic">Nothing yet. Add something small.</p>
                )}
                {byDay[d.key].map((a) => {
                  const c = CATEGORIES[a.category] || CATEGORIES.routine;
                  return (
                    <div
                      key={a.id}
                      data-testid={`activity-${a.id}`}
                      className={`activity-block ${c.bg} ${c.text} border ${c.border} border-l-4 ${c.accent} rounded-xl p-3 print-block`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <button
                          onClick={() => openEdit(a)}
                          className="flex-1 text-left"
                          data-testid={`edit-activity-${a.id}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <IconFor name={a.icon} className="w-4 h-4" />
                            <span className={`font-semibold text-sm ${a.done ? "line-through opacity-70" : ""}`}>{a.title}</span>
                            {a.series_id && (
                              <span title="Repeats every week" aria-label="Repeats every week">
                                <Repeat className="w-3 h-3 opacity-70" />
                              </span>
                            )}
                          </div>
                          <div className="text-xs opacity-90">{fmt(a.start_time)} – {fmt(a.end_time)}</div>
                          {a.reminder && (
                            <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium">
                              <Bell className="w-3 h-3" /> Reminder on
                            </div>
                          )}
                        </button>
                        <button
                          onClick={() => toggleDone(a)}
                          data-testid={`done-${a.id}`}
                          aria-label="Mark done"
                          className="no-print p-1 rounded-full hover:bg-white/60"
                        >
                          {a.done
                            ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            : <Circle className="w-5 h-5 text-slate-400" />}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <ActivityDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </div>
  );
}
